import { InsureMoRegion } from '__generated__/graphql';
import { sub } from 'date-fns';
import { t } from 'utils/locale';
import { Translated } from 'utils/staticText';
import { CheckoutPageStaticText } from './i18n';
import { CarrierCode } from '$/types/travelInsurance';

export const AVAILABLE_REGIONS = [
  'ASEAN',
  'ASIA_PACIFIC',
  'WORLDWIDE_EXCLUDING_USA',
  'WORLDWIDE',
] as InsureMoRegion[];

export const AVAILABLE_NATIONALITY_FLAG_DIMENSIONS = [
  // Original
  'w20',
  'w40',
  'w80',
  'w160',
  'w320',
  'w640',
  'w1280',
  'w2560',
  'h20',
  'h24',
  'h40',
  'h60',
  'h80',
  'h120',
  'h240',
  // Waving
  '16x12',
  '20x15',
  '24x18',
  '28x21',
  '32x24',
  '36x27',
  '40x30',
  '48x36',
  '56x42',
  '60x45',
  '64x48',
  '72x54',
  '80x60',
  '84x63',
  '96x72',
  '108x81',
  '112x84',
  '120x90',
  '128x96',
  '144x108',
  '160x120',
  '192x144',
  '224x168',
  '256x192',
] as const;

/**
 * Nationality to Country code conversion
 * Aim only to fill up those had no flag mapping
 */
export const NATIONALITY_FLAG_MAP = {
  TP: 'TL', //EAST TIMORESE
  GC: 'GB', //BRITISH OVERSEAS TERRITORIES CITIZEN
  GJ: 'GB', //BRITISH PROTECTED PERSON
  UK: 'GB', //BRITISH SUBJECT
  KA: 'KH', //CAMBODIAN
  CB: 'HR', //CROATIAN
  EN: 'EE', //ESTONIAN
  GO: 'GE', //GEORGIAN
  DG: 'DE', //GERMAN
  SW: 'KN', //KITTIAN & NEVISIAN
  LH: 'LT', //LITHUANIAN
  MB: 'MK', //MACEDONIAN
  MJ: 'ME', //MONTENEGRIN
  BU: 'MM', //MYANMAR
  NT: 'NL', //NETHERLANDS
  RF: 'RU', //RUSSIAN
  TI: 'TJ', //TAJIKISTANI
  UR: 'UA', //UKRAINIAN
  YM: 'YE', //YEMENI
};

export enum CheckoutPageStep {
  TripDetailsPage,
  MainInsuredDetailsForm,
  AdditionalInsuredDetailsForm,
  AddOnsPage,
  ReviewPage,
}

export const CheckoutPageStepTracking = {
  [CheckoutPageStep.TripDetailsPage]: {
    number: '1',
    name: 'select-coverage',
  },
  [CheckoutPageStep.MainInsuredDetailsForm]: {
    number: '2',
    name: 'personal-information',
  },
  [CheckoutPageStep.AdditionalInsuredDetailsForm]: {
    number: '2',
    name: 'personal-information',
  },
  [CheckoutPageStep.AddOnsPage]: {
    number: '2a',
    name: 'plan-personalize',
  },
  [CheckoutPageStep.ReviewPage]: {
    number: '3',
    name: 'purchase-review',
  },
};

export const AdultDateOfBirth: Date = sub(new Date(), { years: 18 });
export const ChildDateOfBirth: Date = sub(new Date(), { months: 12 });

export const RELATIONSHIP_WITH_POLICYHOLDER = {
  CHILD: 'CHLD',
  COLLEAGUE: 'CLG',
  EMPLOYEE: 'EMPL',
  EMPLOYER: 'EMPLR',
  FRIEND: 'FRND',
  OTHERS: 'OTH',
  PARENT: 'PRNT',
  RELATIVE: 'REL',
  SELF: 'SLF',
  SIBLING: 'SBLNG',
  SPOUSE: 'SP',
  COMPANION: 'COMPANION',
} as const;

export const MAX_TRAVEL_DAYS = 365;

export const PURCHASE_CONTEXT_STORAGE_KEY = 'successful-ti-purchases';

export const TITLE_MAPPING = ({
  brand,
  providerName,
  staticText,
}: {
  brand: string;
  providerName: string;
  staticText: Translated<typeof CheckoutPageStaticText>;
}): Record<string, string> => ({
  [CheckoutPageStep.TripDetailsPage]: (
    t(staticText.tripDetailsPageTitle, {
      brand,
      providerName,
    }) as string[]
  ).join(''),
  [CheckoutPageStep.MainInsuredDetailsForm]: (
    t(staticText.mainInsuredPageTitle, {
      brand,
      providerName,
    }) as string[]
  ).join(''),
  [CheckoutPageStep.AdditionalInsuredDetailsForm]: (
    t(staticText.additionalTravellerPageTitle, {
      brand,
      providerName,
    }) as string[]
  ).join(''),
  [CheckoutPageStep.ReviewPage]: (
    t(staticText.reviewTitlePage, {
      brand,
      providerName,
    }) as string[]
  ).join(''),
});

export const COVERAGE_BENEFIT_ATTRIBUTES = ({
  brand,
}: {
  brand: string;
}): string[] => [
  'TRAVEL_CANCELLATION',
  'OVERSEAS_MEDICAL_EXPENSES',
  'LOSS_OF_BAGGAGE_OR_PERSONAL_BELONGING',
  ...(brand === 'singsaver' ? ['COVID_19_MEDICAL_EXPENSE'] : []),
  ...(brand === 'moneyhero' ? ['CAR_RENTAL_EXCESS'] : []),
];

export const CHINA_NETWORK_HOSPITAL_ADMISSION_ADD_ON: Partial<
  Record<
    CarrierCode,
    {
      requireHomePermitReturnNumber?: boolean;
    }
  >
> = {
  [CarrierCode.GENX]: {
    requireHomePermitReturnNumber: true,
  },
  [CarrierCode.MHGENERALI]: {
    requireHomePermitReturnNumber: true,
  },
};

export enum SUBMIT_APPLICATION_STATE {
  ERROR_DUPLICATE_POLICY,
  ERROR_GENERIC,
  READY,
  SUBMITTING,
  MANUAL_UNDERWRITING_CONFIRMATION,
  UNDERWRITING_BLACKLISTED,
  PAYMENT,
}

export enum FETCH_PREMIUM_STATE {
  ERROR_GENERIC,
  ERROR_PROVIDER_UNDER_MAINTENANCE,
  ERROR_18_YO_AT_PURCHASE_DAY,
  ERROR_EXCEED_MAX_TRAVELLER,
  FETCHING,
  READY,
}

export const PRE_EX_PRODUCT_SUFFIX = 'PRE-EX';

// CMS-1851: single source of truth for the trip-type rule lives in
// cms-components (direct path — the ApplyCta barrel pulls client-only code).
export {
  ANNUAL_PLAN_CATEGORY_IDENTIFIER,
  SINGLE_PLAN_CATEGORY_IDENTIFIER,
} from '@hyphengroup/cms-components/components/ApplyCta/travelTripTypeFallback';

export const ADD_ONS_TECHNICAL_ID_MAPPING = {
  CHINA_NETWORK_HOSPITAL_ADMISSION: 'coverChinaNetworkHospitalAdmission',
  CRUISE_VACATION: 'coverCruiseVacation',
  FOLLOW_UP_MEDICAL_TREATMENT_IN_SG: 'coverMandatoryCoverage',
  GOLF_PROTECTION: 'coverGolfProtection',
  // GE, as the catalogue entry names it.
  'PRE-EXIST': 'preExisting',
  SCUBA_DIVING: 'coverScubaDiving',
  SNOW_SPORTS: 'coverSnowSports',
};

export const ADD_ONS_PRICING_ID_MAPPING = {
  CRUISE_VACATION: 'cruiseCoverPremium',
  FOLLOW_UP_MEDICAL_TREATMENT_IN_SG: 'mandatoryCoverPremium',
  GOLF_PROTECTION: 'golfCoverPremium',
  'PRE-EXIST': 'preExistingBenefitPremium',
  SCUBA_DIVING: 'scubaDivingCoverPremium',
  SNOW_SPORTS: 'snowSportsCoverPremium',
};

/**
 * Carriers whose add-ons are bought per traveller rather than per policy:
 * the user picks which travellers each add-on covers, and the price shown is
 * per traveller. Selection lives in `InsuredDetails.travellerAddOns`.
 */
export const PER_TRAVELLER_ADD_ON_CARRIERS: CarrierCode[] = [CarrierCode.GE];

export const EXCLUDE_ADD_ONS_DISCOUNT = ['FOLLOW_UP_MEDICAL_TREATMENT_IN_SG'];

export const SEND_DUE_PREMIUM_AS_IS = [
  {
    brand: 'singsaver',
    providerUid: 'MSIG',
  },
  {
    brand: 'singsaver',
    providerUid: 'STARR',
  },
];

export const REGION_DISCLAIMER_KEY_MAPPING: Record<InsureMoRegion, string> = {
  ASEAN: 'asean',
  ASIA_PACIFIC: 'apac',
  WORLDWIDE_EXCLUDING_USA: 'worldwide-exclude-canada-usa',
  WORLDWIDE: 'worldwide',
};
